import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { format } from 'date-fns';
import { useStore } from '../store';
import api from '../lib/api';
import {
  acceptedChecklistCount,
  latestGapDetectionAt,
  needsCrossDocumentClarification,
} from './checklistPresentation';
import TechnicalFileStructure from '../components/TechnicalFileStructure';
import ProjectTasks from '../components/ProjectTasks';
import RegulationPackAssessments from '../components/RegulationPackAssessments';
import {
  REGULATION_SCOPE_LABELS,
  REGULATION_SCOPE_OPTIONS,
  REGULATION_PACK_LABELS,
  REGULATION_PACK_OPTIONS,
  regulationScopeLabel,
  type AuditEvent,
  type AuditResponse,
  type DecisionLog,
  type DecisionLogResponse,
  type ChecklistGapDetectionResult,
  type ChecklistItem,
  type ChecklistResponse,
  type ChecklistStatus,
  type EvidenceDocument,
  type EvidenceResponse,
  type Finding,
  type FindingReviewStatus,
  type FindingType,
  type Project,
  type ProjectRole,
  type RegulationPack,
  type RegulationPackAssessment,
  type RegulationScope,
  type Scan,
  type Risk,
  type Severity,
} from '../types';
import {
  findingRemediation,
  isTransitionReference,
} from './findingPresentation';
import { saveRegulationPackAssessment } from './regulationPackAssessmentFlow';
import axios from 'axios';

const SEVERITY_ORDER: Array<Severity | 'unknown'> = [
  'high',
  'medium',
  'low',
  'unknown',
];

const CHECKLIST_STATUS_ORDER: ChecklistStatus[] = [
  'needs_review',
  'missing',
  'evidence_found',
  'accepted',
  'not_applicable',
  'not_started',
];

type SortKey = 'severity' | 'name';

interface NormalizedRisk {
  name: string;
  type: FindingType | 'legacy_risk';
  severity: Severity | 'unknown';
  standard: string;
  fix: string;
  evidenceExcerpt: string | null;
  sourceLocation: string | null;
  reviewStatus: FindingReviewStatus | 'unknown';
  confidence: number | null;
}

interface NormalizedScan {
  filename: string;
  regulationScopeLabel: string;
  summary: string | null;
  createdAtLabel: string;
  risks: NormalizedRisk[];
  warnings: string[];
}

// Helper function to extract error message from various error types
async function extractReportDownloadError(
  error: unknown,
  t: (key: string) => string
): Promise<string> {
  // Check if it's an Axios error
  if (axios.isAxiosError(error)) {
    // If response data is a Blob, we need to read it
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        // Try to parse as JSON
        if (text) {
          try {
            const parsed = JSON.parse(text);
            // If it's a JSON object with an error property that's a non-empty string, return it
            if (
              parsed.error &&
              typeof parsed.error === 'string' &&
              parsed.error.trim()
            ) {
              return parsed.error;
            }
            // If the blob text itself is non-empty, return it
            return text;
          } catch (parseError) {
            // If JSON parsing fails but we have text, return the text
            return text;
          }
        }
      } catch (blobError) {
        // If reading blob fails, fall through to other error handling
        console.error('Error reading blob:', blobError);
      }
    }

    // If response data is already a string or object with error property
    if (error.response?.data) {
      if (
        typeof error.response.data === 'string' &&
        error.response.data.trim()
      ) {
        return error.response.data;
      }
      if (
        typeof error.response.data === 'object' &&
        error.response.data !== null
      ) {
        const dataObj = error.response.data as { error?: string };
        if (
          dataObj.error &&
          typeof dataObj.error === 'string' &&
          dataObj.error.trim()
        ) {
          return dataObj.error;
        }
      }
    }

    // If we have a response status but no usable data
    if (error.response?.status) {
      return t('errors.network');
    }
  }

  // Fallback to localized generic error message
  return t('results.reportDownloadErrorFallback');
}

export default function ResultsPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentScan, scans, setCurrentScan, user } = useStore();

  const [scan, setScan] = useState<Scan | null>(null);
  const [sortBy, setSortBy] = useState<SortKey>('severity');
  const [scopeUpdateError, setScopeUpdateError] = useState<string | null>(null);
  const [isUpdatingScope, setIsUpdatingScope] = useState(false);
  const [regulationPacks, setRegulationPacks] = useState<RegulationPack[]>([]);

  const [projectRole, setProjectRole] = useState<ProjectRole | null>(null);
  const [regulationPackAssessments, setRegulationPackAssessments] = useState<
    RegulationPackAssessment[]
  >([]);
  const [addingRegulationPack, setAddingRegulationPack] =
    useState<RegulationPack | null>(null);
  const [recentlyAddedRegulationPack, setRecentlyAddedRegulationPack] =
    useState<RegulationPack | null>(null);
  const [assessingRegulationPack, setAssessingRegulationPack] =
    useState<RegulationPack | null>(null);
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [checklistError, setChecklistError] = useState<string | null>(null);
  const [isLoadingChecklist, setIsLoadingChecklist] = useState(false);
  const [isApplyingGapDetection, setIsApplyingGapDetection] = useState(false);
  const [linkingEvidenceKey, setLinkingEvidenceKey] = useState<string | null>(
    null
  );
  const [projectEvidence, setProjectEvidence] = useState<EvidenceDocument[]>(
    []
  );
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const [isLoadingEvidence, setIsLoadingEvidence] = useState(false);
  const [evidenceReloadToken, setEvidenceReloadToken] = useState(0);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);
  const [auditReloadToken, setAuditReloadToken] = useState(0);
  const [decisionLogs, setDecisionLogs] = useState<DecisionLog[]>([]);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [isLoadingDecisions, setIsLoadingDecisions] = useState(false);
  const [decisionReloadToken, setDecisionReloadToken] = useState(0);

  useEffect(() => {
    if (!id) return;

    const inMemory =
      currentScan?.id === id ? currentScan : scans.find(item => item.id === id);

    if (inMemory) {
      setScan(inMemory);
      return;
    }

    api
      .get<Scan>(`/api/scans/${id}`)
      .then(res => {
        setScan(res.data);
        setCurrentScan(res.data);
        // Redirect to processing page if scan is still processing
        if (res.data.status === 'processing' || res.data.status === 'pending') {
          navigate(`/scan/${id}/processing`);
        }
      })
      .catch(() => navigate('/history'));
  }, [currentScan, id, navigate, scans, setCurrentScan]);

  useEffect(() => {
    if (!scan?.projectId) {
      setRegulationPacks([]);
      setRegulationPackAssessments([]);
      setProjectRole(null);
      return;
    }

    let canceled = false;
    api
      .get<Project>(`/api/projects/${scan.projectId}`)
      .then(res => {
        if (!canceled) {
          setRegulationPacks(res.data.regulationPacks ?? []);
          setRegulationPackAssessments(
            res.data.regulationPackAssessments ?? []
          );
          setProjectRole(res.data.currentUserRole);
        }
      })
      .catch(error => console.error(error));

    return () => {
      canceled = true;
    };
  }, [scan?.projectId]);

  const canConfigureProject =
    projectRole === 'owner' || projectRole === 'admin';
  const canReviewProject = canConfigureProject || projectRole === 'reviewer';

  const normalizedScan = useMemo(
    () => (scan ? normalizeScan(scan, t) : null),
    [scan, t]
  );

  const sortedRisks = useMemo(() => {
    if (!normalizedScan) return [];

    return [...normalizedScan.risks].sort((a, b) => {
      if (sortBy === 'severity') {
        return (
          SEVERITY_ORDER.indexOf(a.severity) -
          SEVERITY_ORDER.indexOf(b.severity)
        );
      }

      return a.name.localeCompare(b.name);
    });
  }, [normalizedScan, sortBy]);

  const summary = useMemo(() => {
    const risks = normalizedScan?.risks ?? [];
    return {
      high: risks.filter(risk => risk.severity === 'high').length,
      medium: risks.filter(risk => risk.severity === 'medium').length,
      low: risks.filter(risk => risk.severity === 'low').length,
      total: risks.length,
    };
  }, [normalizedScan]);

  const checklistCounts = useMemo(() => {
    return checklistItems.reduce(
      (counts, item) => {
        counts[item.status] += 1;
        return counts;
      },
      {
        not_started: 0,
        evidence_found: 0,
        missing: 0,
        needs_review: 0,
        not_applicable: 0,
        accepted: 0,
      } satisfies Record<ChecklistStatus, number>
    );
  }, [checklistItems]);
  const resolvedChecklistCount = acceptedChecklistCount(checklistItems);
  const lastGapDetectionAt = latestGapDetectionAt(auditEvents);

  const [reportDownloadError, setReportDownloadError] = useState<string | null>(
    null
  );
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  // Clear error when loading a different scan
  useEffect(() => {
    setReportDownloadError(null);
    setScopeUpdateError(null);
    setChecklistError(null);
    setEvidenceError(null);
    setAuditError(null);
    setDecisionError(null);
  }, [id]);

  useEffect(() => {
    if (scan?.status !== 'done' || !scan.projectId) {
      setChecklistItems([]);
      return;
    }

    let canceled = false;
    setIsLoadingChecklist(true);
    setChecklistError(null);

    api
      .get<ChecklistResponse>(`/api/projects/${scan.projectId}/checklist`)
      .then(res => {
        if (!canceled) setChecklistItems(res.data.checklistItems);
      })
      .catch(error => {
        console.error(error);
        if (!canceled) setChecklistError(t('results.checklist.loadError'));
      })
      .finally(() => {
        if (!canceled) setIsLoadingChecklist(false);
      });

    return () => {
      canceled = true;
    };
  }, [scan?.projectId, scan?.status, t]);

  useEffect(() => {
    if (scan?.status !== 'done' || !scan.projectId) {
      setProjectEvidence([]);
      setIsLoadingEvidence(false);
      return;
    }

    let canceled = false;
    setProjectEvidence([]);
    setIsLoadingEvidence(true);
    setEvidenceError(null);

    api
      .get<EvidenceResponse>(`/api/projects/${scan.projectId}/evidence`)
      .then(res => {
        if (!canceled) setProjectEvidence(res.data.evidenceDocuments);
      })
      .catch(error => {
        console.error(error);
        if (!canceled) setEvidenceError(t('results.technicalFile.loadError'));
      })
      .finally(() => {
        if (!canceled) setIsLoadingEvidence(false);
      });

    return () => {
      canceled = true;
    };
  }, [evidenceReloadToken, scan?.projectId, scan?.status, t]);

  useEffect(() => {
    if (!scan?.projectId) {
      setAuditEvents([]);
      setIsLoadingAudit(false);
      return;
    }

    let canceled = false;
    setIsLoadingAudit(true);
    setAuditError(null);

    api
      .get<AuditResponse>(`/api/projects/${scan.projectId}/audit`)
      .then(res => {
        if (!canceled) setAuditEvents(res.data.auditEvents);
      })
      .catch(error => {
        console.error(error);
        if (!canceled) setAuditError(t('results.audit.loadError'));
      })
      .finally(() => {
        if (!canceled) setIsLoadingAudit(false);
      });

    return () => {
      canceled = true;
    };
  }, [auditReloadToken, scan?.projectId, t]);

  useEffect(() => {
    if (!scan?.projectId) {
      setDecisionLogs([]);
      setIsLoadingDecisions(false);
      return;
    }

    let canceled = false;
    setIsLoadingDecisions(true);
    setDecisionError(null);

    api
      .get<DecisionLogResponse>(`/api/projects/${scan.projectId}/decisions`)
      .then(res => {
        if (!canceled) setDecisionLogs(res.data.decisionLogs);
      })
      .catch(error => {
        console.error(error);
        if (!canceled) setDecisionError(t('results.decisions.loadError'));
      })
      .finally(() => {
        if (!canceled) setIsLoadingDecisions(false);
      });

    return () => {
      canceled = true;
    };
  }, [decisionReloadToken, scan?.projectId, t]);

  const handleScopeChange = async (nextScope: RegulationScope) => {
    if (!scan) return;

    const previousScan = scan;
    const optimisticScan = { ...scan, regulationScope: nextScope };
    setScan(optimisticScan);
    setCurrentScan(optimisticScan);
    setScopeUpdateError(null);

    if (!scan.projectId) return;

    setIsUpdatingScope(true);
    try {
      const res = await api.patch<Project>(
        `/api/projects/${scan.projectId}/regulation-scope`,
        { regulationScope: nextScope }
      );
      const updatedScan = {
        ...optimisticScan,
        regulationScope: res.data.regulationScope,
      };
      setScan(updatedScan);
      setCurrentScan(updatedScan);
      setAuditReloadToken(token => token + 1);
    } catch (error) {
      console.error(error);
      setScan(previousScan);
      setCurrentScan(previousScan);
      setScopeUpdateError(t('results.scopeUpdateError'));
    } finally {
      setIsUpdatingScope(false);
    }
  };

  const handleAddRegulationPack = async (pack: RegulationPack) => {
    if (!scan?.projectId) return;

    setScopeUpdateError(null);
    setAddingRegulationPack(pack);
    try {
      const res = await api.post<Project>(
        `/api/projects/${scan.projectId}/regulation-packs`,
        { regulationPack: pack }
      );
      setRegulationPacks(res.data.regulationPacks ?? []);
      setRecentlyAddedRegulationPack(pack);
      setRegulationPackAssessments(res.data.regulationPackAssessments ?? []);
      const checklist = await api.get<ChecklistResponse>(
        `/api/projects/${scan.projectId}/checklist`
      );
      setChecklistItems(checklist.data.checklistItems);
      setAuditReloadToken(token => token + 1);
    } catch (error) {
      console.error(error);
      setScopeUpdateError(t('results.regulationPackAddError'));
    } finally {
      setAddingRegulationPack(null);
    }
  };

  const handleAssessRegulationPack = async (
    pack: RegulationPack,
    inputs: Record<string, boolean>
  ) => {
    if (!scan?.projectId) return;

    setScopeUpdateError(null);
    setChecklistError(null);
    setAssessingRegulationPack(pack);
    try {
      const result = await saveRegulationPackAssessment({
        client: api,
        projectId: scan.projectId,
        pack,
        inputs,
        onAssessmentSaved: assessment => {
          setRegulationPackAssessments(current => [
            ...current.filter(item => item.regulationPack !== pack),
            assessment,
          ]);
          setAuditReloadToken(token => token + 1);
        },
        onChecklistRefreshed: setChecklistItems,
      });
      if (result.refreshError) {
        console.error(result.refreshError);
        setChecklistError(t('results.packAssessment.refreshError'));
      }
    } catch (error) {
      console.error(error);
      setScopeUpdateError(t('results.packAssessment.error'));
    } finally {
      setAssessingRegulationPack(null);
    }
  };

  const handleDownload = async (reportFormat: 'pdf' | 'docx' = 'pdf') => {
    if (!scan || !normalizedScan) return;

    // Reset error state
    setReportDownloadError(null);
    setIsDownloadingReport(true);

    try {
      const usesProjectReport = Boolean(scan.projectId);
      const endpoint = usesProjectReport
        ? `/api/projects/${scan.projectId}/report/${reportFormat}`
        : `/api/scans/${scan.id}/report`;
      const res = await api.get(endpoint, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = usesProjectReport
        ? `machinery-compliance-report-${scan.projectId?.slice(0, 8)}.${reportFormat}`
        : `compliance-report-${scan.id.slice(0, 8)}.pdf`;
      anchor.click();
      window.URL.revokeObjectURL(url);
      setAuditReloadToken(token => token + 1);
    } catch (error: unknown) {
      // Extract error message using our helper
      const errorMessage = await extractReportDownloadError(error, t);
      setReportDownloadError(errorMessage);
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const handleDeclarationOfPerformanceDownload = async (
    reportFormat: 'pdf' | 'docx'
  ) => {
    if (!scan?.projectId) return;
    setReportDownloadError(null);
    setIsDownloadingReport(true);
    try {
      const res = await api.get(
        `/api/projects/${scan.projectId}/declaration-of-performance/${reportFormat}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `declaration-of-performance-${scan.projectId.slice(0, 8)}.${reportFormat}`;
      anchor.click();
      window.URL.revokeObjectURL(url);
      setAuditReloadToken(token => token + 1);
    } catch (error: unknown) {
      setReportDownloadError(await extractReportDownloadError(error, t));
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const handleToyDeclarationDownload = async (reportFormat: 'pdf' | 'docx') => {
    if (!scan?.projectId) return;
    setReportDownloadError(null);
    setIsDownloadingReport(true);
    try {
      const res = await api.get(
        `/api/projects/${scan.projectId}/toy-declaration/${reportFormat}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `toy-eu-declaration-${scan.projectId.slice(0, 8)}.${reportFormat}`;
      anchor.click();
      window.URL.revokeObjectURL(url);
      setAuditReloadToken(token => token + 1);
    } catch (error: unknown) {
      setReportDownloadError(await extractReportDownloadError(error, t));
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const handleApplyGapDetection = async () => {
    if (!scan?.projectId) return;

    setChecklistError(null);
    setIsApplyingGapDetection(true);
    try {
      const res = await api.post<ChecklistGapDetectionResult>(
        `/api/projects/${scan.projectId}/checklist/gap-detection`
      );
      setChecklistItems(res.data.checklistItems);
      setAuditReloadToken(token => token + 1);
      setRecentlyAddedRegulationPack(null);
    } catch (error) {
      console.error(error);
      setChecklistError(t('results.checklist.gapDetectionError'));
    } finally {
      setIsApplyingGapDetection(false);
    }
  };

  const handleLinkSuggestedEvidence = async (
    checklistItemId: string,
    evidenceDocumentId: string
  ) => {
    if (!scan?.projectId) return;

    const key = `${checklistItemId}:${evidenceDocumentId}`;
    setChecklistError(null);
    setLinkingEvidenceKey(key);
    try {
      const res = await api.post<ChecklistItem>(
        `/api/projects/${scan.projectId}/checklist/${checklistItemId}/evidence/${evidenceDocumentId}`
      );
      setChecklistItems(items =>
        items.map(item => (item.id === checklistItemId ? res.data : item))
      );
      setAuditReloadToken(token => token + 1);
    } catch (error) {
      console.error(error);
      setChecklistError(t('results.checklist.linkSuggestionError'));
    } finally {
      setLinkingEvidenceKey(null);
    }
  };

  if (!scan) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <div
          style={{
            width: 36,
            height: 36,
            border: '3px solid var(--border)',
            borderTopColor: 'var(--accent-primary)',
            borderRadius: '50%',
          }}
          className="animate-spin"
        />
      </div>
    );
  }

  // Show error state if scan failed
  if (scan.status === 'error') {
    return (
      <div className="animate-fade-in">
        <header className="results-header">
          <div className="page-intro" style={{ marginBottom: 0 }}>
            <div className="page-eyebrow">
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: 'var(--danger)',
                  display: 'inline-block',
                }}
              />
              {t('results.analysisFailed')}
            </div>
            <h1
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 32,
                fontWeight: 800,
                color: 'var(--text-primary)',
                letterSpacing: '-0.02em',
                marginBottom: 6,
              }}
            >
              {t('results.failureTitle')}
            </h1>
            <div className="results-meta">
              <span>{regulationScopeLabel(scan.regulationScope)}</span>
              <span style={{ color: 'var(--text-muted)' }}>·</span>
              <span>{scan.filename}</span>
            </div>
          </div>

          <div className="results-actions">
            <Link className="button button-primary" to="/scan">
              + {t('results.newScan')}
            </Link>
          </div>
        </header>

        <section
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: '32px',
            marginTop: 24,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: 'var(--danger-dim)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span style={{ color: 'var(--danger)', fontSize: 16 }}>×</span>
            </div>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 20,
                fontWeight: 700,
                color: 'var(--text-primary)',
                margin: 0,
              }}
            >
              {t('results.failureHeading')}
            </h2>
          </div>

          <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
            {scan.errorMessage || t('results.failureMessage')}
          </p>

          <div className="results-actions">
            <Link className="button button-primary" to="/scan">
              + {t('results.newScan')}
            </Link>
            <Link className="button button-secondary" to="/history">
              {t('results.viewHistory')}
            </Link>
          </div>
        </section>
      </div>
    );
  }

  // Show processing state if scan is not done
  if (scan.status === 'processing' || scan.status === 'pending') {
    // This should not happen as we redirect in the useEffect, but just in case
    return (
      <section className="route-error-card animate-fade-in">
        <span className="route-error-eyebrow">
          {t('results.processingEyebrow')}
        </span>
        <h1 className="route-error-title">{t('results.processingTitle')}</h1>
        <p className="route-error-message">{t('results.processingMessage')}</p>
        <div className="route-error-actions">
          <Link
            className="button button-primary"
            to={`/scan/${scan.id}/processing`}
          >
            {t('results.viewProgress')}
          </Link>
          <Link className="button button-secondary" to="/history">
            {t('routeErrors.actions.goHistory')}
          </Link>
        </div>
      </section>
    );
  }

  if (!normalizedScan) {
    return (
      <section className="route-error-card animate-fade-in">
        <span className="route-error-eyebrow">
          {t('results.unavailableEyebrow')}
        </span>
        <h1 className="route-error-title">{t('results.unavailableTitle')}</h1>
        <p className="route-error-message">{t('results.unavailableMessage')}</p>
        <div className="route-error-actions">
          <Link className="button button-primary" to="/history">
            {t('routeErrors.actions.goHistory')}
          </Link>
          <Link className="button button-secondary" to="/scan">
            {t('routeErrors.actions.newScan')}
          </Link>
        </div>
      </section>
    );
  }

  const highSeverityGapCount = normalizedScan.risks.filter(
    risk => risk.severity === 'high' && risk.type !== 'strength'
  ).length;
  const needsReviewItemCount = checklistItems.filter(
    item => item.status === 'needs_review'
  ).length;
  const missingEvidenceCategoryCount = checklistItems.filter(
    item =>
      item.status !== 'not_applicable' &&
      item.linkedEvidenceDocuments.length === 0
  ).length;

  return (
    <div className="animate-fade-in">
      <header className="results-header">
        <div className="page-intro" style={{ marginBottom: 0 }}>
          <div className="page-eyebrow">
            <span
              style={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                background: 'var(--success)',
                display: 'inline-block',
              }}
            />
            {t('results.analysisComplete')}
          </div>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 32,
              fontWeight: 800,
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
              marginBottom: 6,
            }}
          >
            {t('results.title')}
          </h1>
          <div className="results-meta">
            <span>{normalizedScan.regulationScopeLabel}</span>
            <span style={{ color: 'var(--text-muted)' }}>·</span>
            <span>{normalizedScan.filename}</span>
            <span style={{ color: 'var(--text-muted)' }}>·</span>
            <span>
              {t('results.scannedAt')} {normalizedScan.createdAtLabel}
            </span>
          </div>
        </div>

        <div className="results-actions">
          {scan.projectId && (
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                minWidth: 260,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--text-muted)',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                {t('results.regulationScope')}
              </span>
              <select
                value={scan.regulationScope ?? 'machinery_directive_2006_42_ec'}
                onChange={event =>
                  handleScopeChange(event.target.value as RegulationScope)
                }
                disabled={isUpdatingScope || !canConfigureProject}
                style={{
                  minHeight: 40,
                  background: 'var(--bg-card)',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '0 12px',
                }}
              >
                {REGULATION_SCOPE_OPTIONS.map(scope => (
                  <option key={scope} value={scope}>
                    {REGULATION_SCOPE_LABELS[scope]}
                  </option>
                ))}
              </select>
            </label>
          )}
          {scan.projectId &&
            REGULATION_PACK_OPTIONS.filter(
              pack => !regulationPacks.includes(pack)
            ).map(pack => (
              <button
                key={pack}
                className="button button-secondary"
                onClick={() => handleAddRegulationPack(pack)}
                disabled={addingRegulationPack !== null || !canConfigureProject}
              >
                {addingRegulationPack === pack
                  ? t('results.addingRegulationPack')
                  : t('results.addRegulationPack', {
                      pack: REGULATION_PACK_LABELS[pack],
                    })}
              </button>
            ))}
          {scan.projectId && (
            <a className="button button-secondary" href="#technical-file">
              {t('results.technicalFile.navigationLabel')}
            </a>
          )}
          {scan.projectId ? (
            <>
              <button
                className="button button-secondary"
                onClick={() => handleDownload('pdf')}
                disabled={isDownloadingReport}
              >
                {isDownloadingReport
                  ? t('results.downloadingReport')
                  : `↓ ${t('results.downloadPDF')}`}
              </button>
              <button
                className="button button-secondary"
                onClick={() => handleDownload('docx')}
                disabled={isDownloadingReport}
              >
                {isDownloadingReport
                  ? t('results.downloadingReport')
                  : `↓ ${t('results.downloadDOCX')}`}
              </button>
            </>
          ) : (
            <button
              className="button button-secondary"
              onClick={() => handleDownload('pdf')}
              disabled={isDownloadingReport}
            >
              {isDownloadingReport
                ? t('results.downloadingReport')
                : `↓ ${t('results.downloadReport')}`}
            </button>
          )}
          {scan.projectId &&
            regulationPacks.includes('construction_products_2024_3110') && (
              <>
                <button
                  className="button button-secondary"
                  onClick={() => handleDeclarationOfPerformanceDownload('pdf')}
                  disabled={isDownloadingReport}
                >
                  ↓ {t('results.downloadDeclarationPDF')}
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => handleDeclarationOfPerformanceDownload('docx')}
                  disabled={isDownloadingReport}
                >
                  ↓ {t('results.downloadDeclarationDOCX')}
                </button>
              </>
            )}
          {scan.projectId &&
            regulationPacks.includes('toy_safety_2025_2509') && (
              <>
                <button
                  className="button button-secondary"
                  onClick={() => handleToyDeclarationDownload('pdf')}
                  disabled={isDownloadingReport}
                >
                  ↓ {t('results.downloadToyDeclarationPDF')}
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => handleToyDeclarationDownload('docx')}
                  disabled={isDownloadingReport}
                >
                  ↓ {t('results.downloadToyDeclarationDOCX')}
                </button>
              </>
            )}
          <Link className="button button-primary" to="/scan">
            + {t('results.newScan')}
          </Link>
        </div>
      </header>

      <RegulationPackAssessments
        assessments={regulationPackAssessments}
        savingPack={assessingRegulationPack}
        disabled={!canReviewProject}
        onAssess={handleAssessRegulationPack}
      />

      {/* Display report download error if exists */}
      {reportDownloadError && (
        <div
          role="alert"
          style={{
            marginBottom: 24,
            padding: '14px 16px',
            background: 'var(--danger-dim)',
            border: '1px solid rgba(255,0,0,0.2)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--danger)',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            {t('results.reportDownloadErrorTitle')}
          </div>
          <div style={{ color: 'var(--text-secondary)' }}>
            {reportDownloadError}
          </div>
        </div>
      )}

      {scopeUpdateError && (
        <div
          role="alert"
          style={{
            marginBottom: 24,
            padding: '14px 16px',
            background: 'var(--danger-dim)',
            border: '1px solid rgba(255,0,0,0.2)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--danger)',
          }}
        >
          {scopeUpdateError}
        </div>
      )}

      {normalizedScan.warnings.length > 0 && (
        <div
          style={{
            marginBottom: 24,
            padding: '14px 16px',
            background: 'var(--warning-dim)',
            border: '1px solid rgba(255,179,0,0.2)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--warning)',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            {t('results.partialDataTitle')}
          </div>
          <div style={{ color: 'var(--text-secondary)' }}>
            {normalizedScan.warnings.join(' ')}
          </div>
        </div>
      )}

      {normalizedScan.summary && (
        <section
          className="results-panel"
          style={{ marginBottom: 24, padding: '24px 28px' }}
        >
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 16,
              fontWeight: 700,
              color: 'var(--text-primary)',
              marginBottom: 10,
            }}
          >
            Executive Summary
          </div>
          <p
            style={{
              margin: 0,
              color: 'var(--text-secondary)',
              lineHeight: 1.7,
              fontSize: 15,
            }}
          >
            {normalizedScan.summary}
          </p>
        </section>
      )}

      <section className="results-summary-grid">
        <div
          className="results-action-summary-card"
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: '24px 32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            gap: 10,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--text-primary)',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            {t('results.actionSummary.title')}
          </span>
          <p
            style={{
              margin: 0,
              color: 'var(--text-secondary)',
              fontSize: 14,
              lineHeight: 1.6,
            }}
          >
            {t('results.actionSummary.highGaps', {
              count: highSeverityGapCount,
            })}
            {' · '}
            {t('results.actionSummary.needsReview', {
              count: needsReviewItemCount,
            })}
            {' · '}
            {t('results.actionSummary.missingEvidence', {
              count: missingEvidenceCategoryCount,
            })}
          </p>
        </div>

        {[
          {
            label: t('results.severity.high'),
            count: summary.high,
            color: 'var(--severity-high)',
            dim: 'var(--danger-dim)',
          },
          {
            label: t('results.severity.medium'),
            count: summary.medium,
            color: 'var(--severity-medium)',
            dim: 'var(--warning-dim)',
          },
          {
            label: t('results.severity.low'),
            count: summary.low,
            color: 'var(--severity-low)',
            dim: 'var(--success-dim)',
          },
          {
            label: t('results.total'),
            count: summary.total,
            color: 'var(--text-secondary)',
            dim: 'var(--bg-tertiary)',
          },
        ].map(({ label, count, color, dim }) => (
          <div
            key={label}
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 42,
                fontWeight: 900,
                color,
                lineHeight: 1,
              }}
            >
              {count}
            </span>
            <div
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                background: dim,
                borderRadius: 100,
                fontSize: 11,
                fontWeight: 700,
                color,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                alignSelf: 'flex-start',
              }}
            >
              {label}
            </div>
          </div>
        ))}
      </section>

      {scan.projectId && (
        <TechnicalFileStructure
          documents={projectEvidence}
          isLoading={isLoadingEvidence}
          error={evidenceError}
          projectId={scan.projectId}
          onRetry={() => setEvidenceReloadToken(token => token + 1)}
          onVersionUploaded={() => setEvidenceReloadToken(token => token + 1)}
          regulationPacks={regulationPacks}
        />
      )}

      {recentlyAddedRegulationPack && (
        <section className="results-panel" role="status">
          <strong>New regulation added — run gap detection</strong>
          <p style={{ color: 'var(--text-secondary)' }}>
            Check the uploaded documents against the expanded regulation scope.
          </p>
          <button
            className="button button-primary"
            onClick={() => void handleApplyGapDetection()}
            disabled={isApplyingGapDetection}
          >
            {isApplyingGapDetection
              ? 'Running gap detection…'
              : 'Run gap detection'}
          </button>
        </section>
      )}

      {scan.projectId && (
        <ProjectTasks
          projectId={scan.projectId}
          currentUserId={user?.id}
          checklistItems={checklistItems}
          findings={scan.findings ?? []}
          evidenceDocuments={projectEvidence}
          projectRole={projectRole}
          onTasksChanged={() => setAuditReloadToken(token => token + 1)}
        />
      )}

      {scan.projectId && (
        <section
          className="results-panel"
          style={{ marginBottom: 24, padding: '24px 28px' }}
          aria-labelledby="project-decisions-heading"
        >
          <div className="results-toolbar">
            <div>
              <h2
                id="project-decisions-heading"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 16,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                }}
              >
                {t('results.decisions.title')}
              </h2>
              <p
                style={{
                  margin: '6px 0 0',
                  color: 'var(--text-muted)',
                  fontSize: 13,
                }}
              >
                {t('results.decisions.subtitle')}
              </p>
            </div>
            {decisionError && (
              <button
                className="button button-secondary"
                onClick={() => setDecisionReloadToken(token => token + 1)}
                style={{ minHeight: 'auto', padding: '8px 14px' }}
              >
                {t('results.decisions.retry')}
              </button>
            )}
          </div>

          {isLoadingDecisions ? (
            <p style={{ color: 'var(--text-muted)' }}>
              {t('results.decisions.loading')}
            </p>
          ) : decisionError ? (
            <p role="alert" style={{ color: 'var(--danger)' }}>
              {decisionError}
            </p>
          ) : decisionLogs.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>
              {t('results.decisions.empty')}
            </p>
          ) : (
            <ol
              style={{
                listStyle: 'none',
                margin: '20px 0 0',
                padding: 0,
                display: 'grid',
                gap: 14,
              }}
            >
              {decisionLogs.map(decision => (
                <li
                  key={decision.id}
                  style={{
                    borderLeft: '3px solid var(--accent-primary)',
                    padding: '2px 0 2px 16px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 16,
                      flexWrap: 'wrap',
                    }}
                  >
                    <strong style={{ color: 'var(--text-primary)' }}>
                      {t(`results.decisions.events.${decision.decisionType}`)}
                    </strong>
                    <time
                      dateTime={decision.createdAt}
                      style={{ color: 'var(--text-muted)', fontSize: 12 }}
                    >
                      {formatAuditTimestamp(decision.createdAt)}
                    </time>
                  </div>
                  {decisionLogDetail(decision) && (
                    <p
                      style={{
                        margin: '5px 0 0',
                        color: 'var(--text-secondary)',
                        fontSize: 13,
                      }}
                    >
                      {decisionLogDetail(decision)}
                    </p>
                  )}
                  <p
                    style={{
                      margin: '5px 0 0',
                      color: 'var(--text-muted)',
                      fontSize: 12,
                    }}
                  >
                    {t('results.audit.actor', {
                      actor: auditActorLabel(decision.actorName, t),
                    })}
                  </p>
                  <blockquote
                    style={{
                      margin: '8px 0 0',
                      padding: '8px 12px',
                      background: 'var(--bg-secondary)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--text-primary)',
                      fontSize: 13,
                    }}
                  >
                    {decision.rationale || t('results.decisions.noRationale')}
                  </blockquote>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}

      {scan.projectId && (
        <section
          className="results-panel"
          style={{ marginBottom: 24, padding: '24px 28px' }}
          aria-labelledby="project-audit-heading"
        >
          <div className="results-toolbar">
            <div>
              <h2
                id="project-audit-heading"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 16,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                }}
              >
                {t('results.audit.title')}
              </h2>
              <p
                style={{
                  margin: '6px 0 0',
                  color: 'var(--text-muted)',
                  fontSize: 13,
                }}
              >
                {t('results.audit.subtitle')}
              </p>
            </div>
            {auditError && (
              <button
                className="button button-secondary"
                onClick={() => setAuditReloadToken(token => token + 1)}
                style={{ minHeight: 'auto', padding: '8px 14px' }}
              >
                {t('results.audit.retry')}
              </button>
            )}
          </div>

          {isLoadingAudit ? (
            <p style={{ color: 'var(--text-muted)' }}>
              {t('results.audit.loading')}
            </p>
          ) : auditError ? (
            <p role="alert" style={{ color: 'var(--danger)' }}>
              {auditError}
            </p>
          ) : auditEvents.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>
              {t('results.audit.empty')}
            </p>
          ) : (
            <ol
              style={{
                listStyle: 'none',
                margin: '20px 0 0',
                padding: 0,
                display: 'grid',
                gap: 12,
              }}
            >
              {auditEvents.map(event => (
                <li
                  key={event.id}
                  style={{
                    borderLeft: '3px solid var(--border)',
                    padding: '2px 0 2px 16px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 16,
                      flexWrap: 'wrap',
                    }}
                  >
                    <strong style={{ color: 'var(--text-primary)' }}>
                      {t(`results.audit.events.${event.eventType}`)}
                    </strong>
                    <time
                      dateTime={event.createdAt}
                      style={{ color: 'var(--text-muted)', fontSize: 12 }}
                    >
                      {formatAuditTimestamp(event.createdAt)}
                    </time>
                  </div>
                  {auditEventDetail(event, t) && (
                    <p
                      style={{
                        margin: '5px 0 0',
                        color: 'var(--text-secondary)',
                        fontSize: 13,
                      }}
                    >
                      {auditEventDetail(event, t)}
                    </p>
                  )}
                  <p
                    style={{
                      margin: '5px 0 0',
                      color: 'var(--text-muted)',
                      fontSize: 12,
                    }}
                  >
                    {t('results.audit.actor', {
                      actor: auditActorLabel(event.actorName, t),
                    })}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}

      {scan.projectId && (
        <section
          className="results-panel"
          style={{ marginBottom: 24, padding: '24px 28px' }}
        >
          <div className="results-toolbar">
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 16,
                fontWeight: 700,
                color: 'var(--text-primary)',
              }}
            >
              {t('results.checklist.title')}
            </h2>

            <button
              className="button button-secondary"
              onClick={handleApplyGapDetection}
              disabled={isApplyingGapDetection || isLoadingChecklist}
              style={{
                minHeight: 'auto',
                padding: '8px 14px',
              }}
            >
              {isApplyingGapDetection
                ? t('results.checklist.runningGapDetection')
                : t(
                    lastGapDetectionAt
                      ? 'results.checklist.rerunGapDetection'
                      : 'results.checklist.runGapDetection'
                  )}
            </button>
            {lastGapDetectionAt && (
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                {t('results.checklist.lastRun', {
                  time: format(new Date(lastGapDetectionAt), 'PPp'),
                })}
              </span>
            )}
          </div>

          {checklistError && (
            <div
              role="alert"
              style={{
                marginBottom: 16,
                padding: '12px 14px',
                background: 'var(--danger-dim)',
                border: '1px solid rgba(255,0,0,0.2)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--danger)',
              }}
            >
              {checklistError}
            </div>
          )}

          {isLoadingChecklist ? (
            <div style={{ color: 'var(--text-muted)', padding: '20px 0' }}>
              {t('results.checklist.loading')}
            </div>
          ) : checklistItems.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', padding: '20px 0' }}>
              {t('results.checklist.empty')}
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  marginBottom: 18,
                }}
              >
                {CHECKLIST_STATUS_ORDER.filter(
                  status => checklistCounts[status] > 0
                ).map(status => (
                  <span
                    key={status}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '6px 10px',
                      color: 'var(--text-secondary)',
                      background: 'var(--bg-secondary)',
                      fontSize: 12,
                    }}
                  >
                    <ChecklistStatusPill status={status} />
                    <strong style={{ color: 'var(--text-primary)' }}>
                      {checklistCounts[status]}
                    </strong>
                  </span>
                ))}
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                {t('results.checklist.resolvedCount', {
                  resolved: resolvedChecklistCount,
                  total: checklistItems.length,
                })}
              </p>

              <div style={{ display: 'grid', gap: 10 }}>
                {checklistItems.map(item => (
                  <div
                    key={item.id}
                    style={{
                      borderTop: '1px solid var(--border)',
                      paddingTop: 12,
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto',
                      gap: 16,
                      alignItems: 'start',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      {item.requirementReference !== item.title && (
                        <span
                          style={{
                            display: 'inline-flex',
                            padding: '3px 7px',
                            marginBottom: 6,
                            borderRadius: 'var(--radius-sm)',
                            background: 'var(--bg-secondary)',
                            color: 'var(--text-muted)',
                            fontFamily: 'var(--font-mono)',
                            fontSize: 11,
                          }}
                        >
                          {item.requirementReference}
                        </span>
                      )}
                      <h3
                        style={{
                          margin: 0,
                          color: 'var(--text-primary)',
                          fontSize: 14,
                          lineHeight: 1.4,
                        }}
                      >
                        {item.title}
                      </h3>
                      {item.statusRationale && (
                        <p
                          style={{
                            margin: '6px 0 0',
                            color: 'var(--text-secondary)',
                            fontSize: 13,
                            lineHeight: 1.5,
                          }}
                        >
                          {item.statusRationale}
                        </p>
                      )}
                      {needsCrossDocumentClarification(item) && (
                        <p
                          style={{
                            margin: '6px 0 0',
                            color: 'var(--text-muted)',
                            fontSize: 13,
                            lineHeight: 1.5,
                          }}
                        >
                          {t('results.checklist.crossDocumentClarification')}
                        </p>
                      )}
                      {item.linkedEvidenceDocuments.length > 0 && (
                        <div
                          style={{
                            marginTop: 8,
                            color: 'var(--text-muted)',
                            fontSize: 12,
                          }}
                        >
                          {t('results.checklist.evidenceCount', {
                            count: item.linkedEvidenceDocuments.length,
                          })}
                        </div>
                      )}
                      {(item.suggestedEvidenceDocuments ?? []).map(document => {
                        const linkKey = `${item.id}:${document.id}`;
                        const isLinking = linkingEvidenceKey === linkKey;
                        return (
                          <div
                            key={document.id}
                            style={{
                              marginTop: 10,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              flexWrap: 'wrap',
                              color: 'var(--text-secondary)',
                              fontSize: 12,
                            }}
                          >
                            <span>
                              {t('results.checklist.suggestedEvidence', {
                                filename: document.filename,
                              })}
                            </span>
                            <button
                              type="button"
                              className="button button-secondary"
                              disabled={Boolean(linkingEvidenceKey)}
                              onClick={() =>
                                handleLinkSuggestedEvidence(
                                  item.id,
                                  document.id
                                )
                              }
                              style={{ padding: '5px 9px', fontSize: 11 }}
                            >
                              {isLinking
                                ? t('results.checklist.linkingEvidence')
                                : t('results.checklist.linkEvidence')}
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    <ChecklistStatusPill status={item.status} />
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      <section className="results-panel">
        <div className="results-toolbar">
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 16,
              fontWeight: 700,
              color: 'var(--text-primary)',
            }}
          >
            {t('results.findingsFound')}
          </h2>

          <div className="stack-actions">
            {(['severity', 'name'] as const).map(value => (
              <button
                key={value}
                onClick={() => setSortBy(value)}
                className={
                  sortBy === value
                    ? 'button button-secondary'
                    : 'button button-secondary'
                }
                style={{
                  minHeight: 'auto',
                  padding: '8px 14px',
                  borderColor:
                    sortBy === value ? 'rgba(0,200,255,0.3)' : 'var(--border)',
                  color:
                    sortBy === value
                      ? 'var(--accent-primary)'
                      : 'var(--text-muted)',
                  background:
                    sortBy === value ? 'var(--accent-dim)' : 'var(--bg-card)',
                }}
              >
                {t('results.sortBy')} {t(`results.sort.${value}`)}
              </button>
            ))}
          </div>
        </div>

        {sortedRisks.length === 0 ? (
          <div
            style={{
              padding: 60,
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontSize: 16,
            }}
          >
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            {t('results.noFindings')}
          </div>
        ) : (
          <>
            <div className="desktop-only">
              <div className="results-desktop-table-head">
                <span>{t('results.columns.finding')}</span>
                <span>{t('results.columns.severity')}</span>
                <span>{t('results.columns.standard')}</span>
                <span>{t('results.columns.fix')}</span>
              </div>
              {sortedRisks.map((risk, index) => (
                <DesktopRiskRow
                  key={`${risk.name}-${index}`}
                  index={index}
                  risk={risk}
                />
              ))}
            </div>

            <div className="mobile-only results-mobile-list">
              {sortedRisks.map((risk, index) => (
                <article
                  key={`${risk.name}-${index}`}
                  className="results-mobile-card"
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 12,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--text-muted)',
                          fontFamily: 'var(--font-mono)',
                          marginBottom: 6,
                        }}
                      >
                        {String(index + 1).padStart(2, '0')}
                      </div>
                      <h3
                        style={{ fontSize: 16, color: 'var(--text-primary)' }}
                      >
                        {risk.name}
                      </h3>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <FindingTypePill type={risk.type} />
                        <FindingReviewStatusPill status={risk.reviewStatus} />
                        {isTransitionReference(risk.standard) && (
                          <span className="finding-transition-badge">
                            2023/1230
                          </span>
                        )}
                      </div>
                    </div>
                    <SeverityPill severity={risk.severity} />
                  </div>

                  <div className="results-mobile-meta">
                    <div className="label-stack">
                      <span className="label-stack-label">
                        {t('results.columns.standard')}
                      </span>
                      <span
                        style={{
                          color: 'var(--accent-primary)',
                          fontFamily: 'var(--font-mono)',
                          fontSize: 13,
                          wordBreak: 'break-word',
                        }}
                      >
                        {risk.standard}
                      </span>
                    </div>
                    <div className="label-stack">
                      <span className="label-stack-label">
                        {t('results.columns.fix')}
                      </span>
                      <span
                        style={{ color: 'var(--text-secondary)', fontSize: 14 }}
                      >
                        {risk.fix}
                      </span>
                    </div>
                    <div className="label-stack">
                      <span className="label-stack-label">Where to update</span>
                      <span
                        style={{ color: 'var(--text-secondary)', fontSize: 14 }}
                      >
                        {
                          findingRemediation({
                            recommendation: risk.fix,
                            sourceLocation: risk.sourceLocation,
                            requirementReference: risk.standard,
                          }).location
                        }
                      </span>
                    </div>
                    {risk.evidenceExcerpt && (
                      <div className="label-stack">
                        <span className="label-stack-label">
                          {t('results.columns.evidence')}
                        </span>
                        <span
                          style={{
                            color: 'var(--text-secondary)',
                            fontSize: 14,
                          }}
                        >
                          {risk.evidenceExcerpt}
                        </span>
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function DesktopRiskRow({
  risk,
  index,
}: {
  risk: NormalizedRisk;
  index: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const remediation = findingRemediation({
    recommendation: risk.fix,
    sourceLocation: risk.sourceLocation,
    requirementReference: risk.standard,
  });

  return (
    <div
      onClick={() => setExpanded(value => !value)}
      style={{
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
        transition: 'background 0.15s',
        animation: `fadeIn 0.3s ease ${index * 0.05}s backwards`,
      }}
      onMouseOver={event => {
        event.currentTarget.style.background = 'var(--bg-card-hover)';
      }}
      onMouseOut={event => {
        event.currentTarget.style.background = 'transparent';
      }}
    >
      <div className="results-desktop-row">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <span
            style={{
              color: 'var(--text-muted)',
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              flexShrink: 0,
              marginTop: 3,
            }}
          >
            {String(index + 1).padStart(2, '0')}
          </span>
          <div style={{ display: 'grid', gap: 8 }}>
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--text-primary)',
                lineHeight: 1.5,
              }}
            >
              {risk.name}
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <FindingTypePill type={risk.type} />
              <FindingReviewStatusPill status={risk.reviewStatus} />
              {isTransitionReference(risk.standard) && (
                <span className="finding-transition-badge">2023/1230</span>
              )}
            </div>
          </div>
        </div>

        <SeverityPill severity={risk.severity} />

        <span
          style={{
            fontSize: 12,
            fontFamily: 'var(--font-mono)',
            color: 'var(--accent-primary)',
            lineHeight: 1.5,
            wordBreak: 'break-word',
          }}
        >
          {risk.standard}
        </span>

        <span
          style={{
            fontSize: 13,
            color: 'var(--text-secondary)',
            lineHeight: 1.5,
          }}
        >
          {risk.fix}
        </span>
      </div>

      {expanded && (
        <div
          style={{
            padding: '14px 24px 16px 54px',
            fontSize: 13,
            color: 'var(--text-secondary)',
            borderTop: '1px solid var(--border)',
            lineHeight: 1.7,
            background: 'var(--bg-secondary)',
          }}
        >
          <div>
            <strong
              style={{
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-display)',
              }}
            >
              Next action:
            </strong>{' '}
            {remediation.nextAction}
          </div>
          <div style={{ marginTop: 8 }}>
            <strong
              style={{
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-display)',
              }}
            >
              Where to update:
            </strong>{' '}
            {remediation.location}
          </div>
          {risk.evidenceExcerpt && (
            <div style={{ marginTop: 8 }}>
              <strong
                style={{
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-display)',
                }}
              >
                Evidence:
              </strong>{' '}
              {risk.evidenceExcerpt}
            </div>
          )}
          {risk.sourceLocation && (
            <div style={{ marginTop: 8 }}>
              <strong
                style={{
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-display)',
                }}
              >
                Source Location:
              </strong>{' '}
              {risk.sourceLocation}
            </div>
          )}
          {risk.confidence !== null && (
            <div style={{ marginTop: 8 }}>
              <strong
                style={{
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-display)',
                }}
              >
                Confidence:
              </strong>{' '}
              {Math.round(risk.confidence * 100)}%
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <strong
              style={{
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-display)',
              }}
            >
              Applicable Standard:
            </strong>{' '}
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                color: 'var(--accent-primary)',
              }}
            >
              {remediation.reference}
            </code>
          </div>
        </div>
      )}
    </div>
  );
}

function FindingTypePill({ type }: { type: FindingType | 'legacy_risk' }) {
  const { t } = useTranslation();
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 9px',
        background: 'var(--bg-tertiary)',
        color: 'var(--text-secondary)',
        borderRadius: 100,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        border: '1px solid var(--border)',
        whiteSpace: 'nowrap',
      }}
    >
      {t(`results.findingTypes.${type}`)}
    </span>
  );
}

function FindingReviewStatusPill({
  status,
}: {
  status: FindingReviewStatus | 'unknown';
}) {
  const { t } = useTranslation();
  const tone =
    status === 'needs_review'
      ? { color: 'var(--severity-medium)', bg: 'var(--warning-dim)' }
      : status === 'accepted'
        ? { color: 'var(--success)', bg: 'var(--success-dim)' }
        : status === 'rejected'
          ? { color: 'var(--severity-high)', bg: 'var(--danger-dim)' }
          : { color: 'var(--accent-primary)', bg: 'var(--accent-dim)' };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 9px',
        background: tone.bg,
        color: tone.color,
        borderRadius: 100,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        border: `1px solid ${tone.color}33`,
        whiteSpace: 'nowrap',
      }}
    >
      {t(`results.reviewStatus.${status}`)}
    </span>
  );
}

function SeverityPill({ severity }: { severity: Severity | 'unknown' }) {
  const { t } = useTranslation();
  const tone =
    severity === 'high'
      ? { color: 'var(--severity-high)', bg: 'var(--danger-dim)' }
      : severity === 'medium'
        ? { color: 'var(--severity-medium)', bg: 'var(--warning-dim)' }
        : severity === 'low'
          ? { color: 'var(--severity-low)', bg: 'var(--success-dim)' }
          : { color: 'var(--text-muted)', bg: 'var(--bg-tertiary)' };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 10px',
        background: tone.bg,
        color: tone.color,
        borderRadius: 100,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        alignSelf: 'flex-start',
        border: `1px solid ${tone.color}33`,
      }}
    >
      {severity === 'unknown'
        ? t('results.sort.unknown')
        : t(`results.severity.${severity}`)}
    </span>
  );
}

function formatAuditTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return format(date, 'PPp');
}

function auditActorLabel(actorName: string, t: TFunction): string {
  if (actorName === 'AI analysis') return t('results.audit.aiAnalysis');
  return actorName || t('results.audit.system');
}

function auditEventDetail(event: AuditEvent, t: TFunction): string | null {
  const metadata = event.metadata;
  const filename = metadata.filename;
  if (typeof filename === 'string' && filename.trim()) return filename;

  if (event.eventType === 'checklist_status_changed') {
    const oldStatus = metadata.oldStatus;
    const newStatus = metadata.newStatus;
    if (typeof oldStatus === 'string' && typeof newStatus === 'string') {
      return `${oldStatus.split('_').join(' ')} → ${newStatus.split('_').join(' ')}`;
    }
  }

  if (event.eventType === 'project_regulation_scope_changed') {
    const oldScope = metadata.oldRegulationScope;
    const newScope = metadata.newRegulationScope;
    if (typeof oldScope === 'string' && typeof newScope === 'string') {
      return `${regulationScopeLabel(oldScope as RegulationScope)} → ${regulationScopeLabel(newScope as RegulationScope)}`;
    }
  }

  const reportFormat = metadata.format;
  if (typeof reportFormat === 'string' && reportFormat.trim()) {
    return t(`results.audit.formats.${reportFormat.toLowerCase()}`, {
      defaultValue: reportFormat.toUpperCase().split('_').join(' '),
    });
  }

  const findingCount = metadata.findingCount;
  if (typeof findingCount === 'number') {
    return `${findingCount} findings`;
  }

  const recommendationCount = metadata.recommendationCount;
  if (typeof recommendationCount === 'number') {
    return `${recommendationCount} recommendations`;
  }

  return null;
}

function decisionLogDetail(decision: DecisionLog): string | null {
  if (decision.previousState && decision.newState) {
    return `${decision.previousState.split('_').join(' ')} → ${decision.newState.split('_').join(' ')}`;
  }

  if (decision.targetType && decision.targetId) {
    return `${decision.targetType.split('_').join(' ')} ${decision.targetId.slice(0, 8)}`;
  }

  return null;
}

function ChecklistStatusPill({ status }: { status: ChecklistStatus }) {
  const { t } = useTranslation();
  const tone = checklistStatusTone(status);

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 9px',
        background: tone.bg,
        color: tone.color,
        borderRadius: 100,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        alignSelf: 'flex-start',
        border: `1px solid ${tone.color}33`,
        whiteSpace: 'nowrap',
      }}
    >
      {t(`results.checklist.status.${status}`)}
    </span>
  );
}

function checklistStatusTone(status: ChecklistStatus) {
  switch (status) {
    case 'needs_review':
      return { color: 'var(--severity-medium)', bg: 'var(--warning-dim)' };
    case 'missing':
      return { color: 'var(--severity-high)', bg: 'var(--danger-dim)' };
    case 'evidence_found':
      return { color: 'var(--success)', bg: 'var(--success-dim)' };
    case 'accepted':
      return { color: 'var(--accent-primary)', bg: 'var(--accent-dim)' };
    case 'not_applicable':
      return { color: 'var(--text-muted)', bg: 'var(--bg-tertiary)' };
    default:
      return { color: 'var(--text-secondary)', bg: 'var(--bg-secondary)' };
  }
}

function normalizeScan(
  scan: Scan,
  t: (key: string) => string
): NormalizedScan | null {
  if (!scan || typeof scan !== 'object') return null;

  const warnings: string[] = [];

  const filename =
    typeof scan.filename === 'string' && scan.filename.trim().length > 0
      ? scan.filename
      : t('results.unknownDocument');

  const summary =
    typeof scan.summary === 'string' && scan.summary.trim().length > 0
      ? scan.summary.trim()
      : null;

  const createdAtDate = new Date(scan.createdAt);
  const createdAtLabel = Number.isNaN(createdAtDate.getTime())
    ? t('results.unknownDate')
    : format(createdAtDate, 'MMM d, yyyy HH:mm');

  if (createdAtLabel === t('results.unknownDate')) {
    warnings.push(t('results.partialDataDate'));
  }

  const rawFindings = Array.isArray(scan.findings) ? scan.findings : [];
  const rawRisks = Array.isArray(scan.risks) ? scan.risks : [];
  if (!Array.isArray(scan.risks)) {
    warnings.push(t('results.partialDataRisks'));
  }

  const risks =
    rawFindings.length > 0
      ? rawFindings.map(finding => normalizeFinding(finding, t))
      : rawRisks.map(risk => normalizeRisk(risk, t));

  return {
    filename,
    regulationScopeLabel: regulationScopeLabel(scan.regulationScope),
    summary,
    createdAtLabel,
    risks,
    warnings,
  };
}

function normalizeRisk(risk: Risk, t: (key: string) => string): NormalizedRisk {
  const severity = normalizeSeverity(risk?.severity);

  return {
    name:
      typeof risk?.name === 'string' && risk.name.trim().length > 0
        ? risk.name
        : t('results.unknownRisk'),
    type: 'legacy_risk',
    severity,
    standard:
      typeof risk?.standard === 'string' && risk.standard.trim().length > 0
        ? risk.standard
        : t('results.unknownStandard'),
    fix:
      typeof risk?.fix === 'string' && risk.fix.trim().length > 0
        ? risk.fix
        : t('results.unknownFix'),
    evidenceExcerpt: null,
    sourceLocation: null,
    reviewStatus:
      severity === 'high' || severity === 'medium' ? 'needs_review' : 'unknown',
    confidence: null,
  };
}

function normalizeFinding(
  finding: Finding,
  t: (key: string) => string
): NormalizedRisk {
  const severity = normalizeSeverity(finding?.severity);

  return {
    name:
      typeof finding?.title === 'string' && finding.title.trim().length > 0
        ? finding.title
        : t('results.unknownFinding'),
    type: normalizeFindingType(finding?.type),
    severity,
    standard:
      typeof finding?.requirementReference === 'string' &&
      finding.requirementReference.trim().length > 0
        ? finding.requirementReference
        : t('results.unknownStandard'),
    fix:
      typeof finding?.recommendation === 'string' &&
      finding.recommendation.trim().length > 0
        ? finding.recommendation
        : t('results.unknownFix'),
    evidenceExcerpt:
      typeof finding?.evidenceExcerpt === 'string' &&
      finding.evidenceExcerpt.trim().length > 0
        ? finding.evidenceExcerpt
        : null,
    sourceLocation:
      typeof finding?.sourceLocation === 'string' &&
      finding.sourceLocation.trim().length > 0
        ? finding.sourceLocation
        : null,
    reviewStatus: normalizeFindingReviewStatus(finding?.reviewStatus),
    confidence:
      typeof finding?.confidence === 'number' &&
      Number.isFinite(finding.confidence)
        ? Math.max(0, Math.min(1, finding.confidence))
        : null,
  };
}

function normalizeSeverity(value: unknown): Severity | 'unknown' {
  return value === 'high' || value === 'medium' || value === 'low'
    ? value
    : 'unknown';
}

function normalizeFindingType(value: unknown): FindingType | 'legacy_risk' {
  return value === 'gap' ||
    value === 'strength' ||
    value === 'needs_review' ||
    value === 'classification' ||
    value === 'extraction_warning'
    ? value
    : 'needs_review';
}

function normalizeFindingReviewStatus(
  value: unknown
): FindingReviewStatus | 'unknown' {
  return value === 'suggested' ||
    value === 'needs_review' ||
    value === 'accepted' ||
    value === 'rejected'
    ? value
    : 'unknown';
}
